import { Fragment, useState, MouseEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import { useHistory, Link } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import classnames from 'classnames';
import {
  Table,
  Tag,
  TableOperationHeader,
  OverflowMenu,
  OverflowMenuItem,
  Button,
  Avatar,
  Typography,
  tailwindOverride,
} from '@getsynapse/design-system';
import { Form, SortingOrderType, SortingType } from 'utils/customTypes';
import { isUserStatusDisabled } from 'utils/functions';
import {
  DATE,
  FORMS_FILTER_OPTIONS,
  PATHS,
  V2_PATHS,
  FORMS_TABLE_OPTIONS,
} from 'utils/constants';
import { selectOrganizationId } from 'state/User/userSlice';
import { showNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import UserAvatar from 'Atoms/UserAvatar';
import RenderNoRecords from 'Atoms/NoRecords/NoRecords';
import { DeletedUser } from 'Atoms/DeletedUser';
import cognotaAvatar from 'assets/images/cognota-avatar.svg';
import MoreActionsPopup from 'Organisms/TableHeaderActionButtons/MoreActionsPopup';
import TruncatedTableColumn from 'Pages/ProjectsListPage/components/ActiveProjects/components/TruncatedTableColumn/TruncatedTableColumn';
import EmptyForms from './EmptyForms';
import DuplicateFormModal from './DuplicateFormModal';
import DeleteFormModal from './DeleteFormModal';
import noFormsFound from 'assets/images/no-requests-found.svg';

type FormsTableProps = {
  forms: Form[];
  onSearch: (title: string) => void;
  onSort: (sorting: SortingOrderType) => void;
  onDuplicate: (
    title: string,
    formId: string,
    redirect: boolean,
    isJsonSchema?: boolean
  ) => void;
  onDelete: (formId: string, isJsonSchema?: boolean) => void;
};
const FormsTable = ({
  forms,
  onSearch,
  onSort,
  onDuplicate,
  onDelete,
}: FormsTableProps) => {
  const { advancedIntake = false, legacyIntake = false } = useFlags();
  const history = useHistory();
  const dispatch = useDispatch();
  // Advanced form creation is the default; only fall back to the legacy form
  // when advanced-intake is off and legacy-intake is on. The legacy option is
  // offered alongside the advanced one (split button) only when both are on.
  const isAdvancedCreate = advancedIntake || !legacyIntake;
  const hasLegacyOption = advancedIntake && legacyIntake;
  const goToAdvancedForm = () => {
    window.location.href = V2_PATHS.FORM_BUILDER;
  };
  const goToLegacyForm = () => {
    history.push(`${PATHS.SETTINGS}${PATHS.FORM_PAGE}`);
  };
  const [search, setSearch] = useState('');
  const [modalData, setModalData] = useState<{
    formName?: string;
    formId: string;
    openOption: string;
    isPublished?: boolean;
    isJsonSchema?: boolean;
  }>({
    formId: '',
    openOption: '',
  });
  const status: Record<string, string> = {
    true: intl.get('SETTINGS_PAGE.FORMS.ACTIVE'),
    false: intl.get('SETTINGS_PAGE.FORMS.DEACTIVATED'),
  };

  const statusColor: Record<
    string,
    { className: string; textClassName: string }
  > = {
    true: {
      className: tailwindOverride(
        'bg-success-lightest border border-transparent py-0.5',
        'group-hover:border-success-dark'
      ),
      textClassName: 'text-success-darker',
    },
    false: {
      className: tailwindOverride(
        'bg-tertiary-lightest border border-transparent py-0.5',
        'group-hover:border-tertiary'
      ),
      textClassName: 'text-tertiary-darker',
    },
  };

  const onTitleSearch = (value: string) => {
    setSearch(value);
    onSearch(value);
  };

  const handleSort = (orderByParam: string, order: SortingType) => {
    onSort({ order, orderBy: orderByParam });
  };

  const handleCopyLink = async (form: Form) => {
    const url = `${window.location.origin}${V2_PATHS.NEW_REQUEST}/${form.id}`;
    try {
      await navigator.clipboard.writeText(url);
      dispatch(
        showNotification({
          notificationVariant: 'success',
          notificationTitle: intl.get(
            'SETTINGS_PAGE.FORMS.COPY_LINK.LINK_COPIED'
          ),
        })
      );
    } catch {
      dispatch(
        showNotification({
          notificationVariant: 'error',
          notificationTitle: intl.get(
            'SETTINGS_PAGE.FORMS.COPY_LINK.COPY_FAILED'
          ),
        })
      );
    }
  };

  const handleMenuSelect = (
    option: typeof FORMS_TABLE_OPTIONS[number],
    form: Form
  ) => {
    switch (option) {
      case 'COPY_LINK':
        handleCopyLink(form);
        break;
      case 'DUPLICATE':
        setModalData({
          formName: get(form, 'title'),
          formId: get(form, 'id') || '',
          openOption: 'DUPLICATE',
          isJsonSchema: get(form, 'isJsonSchema'),
        });
        break;
      case 'DELETE':
        setModalData({
          formId: get(form, 'id') || '',
          openOption: 'DELETE',
          isPublished: get(form, 'data.published'),
          isJsonSchema: get(form, 'isJsonSchema'),
        });
        break;
      default:
        break;
    }
  };

  const isTableEmpty = forms.length === 0;
  const organizationId = useSelector(selectOrganizationId);

  return (
    <Fragment>
      <DuplicateFormModal
        {...modalData}
        isOpen={modalData.openOption === 'DUPLICATE'}
        onClose={() =>
          setModalData((prevData) => ({ ...prevData, openOption: '' }))
        }
        onDuplicate={onDuplicate}
      />
      <DeleteFormModal
        {...modalData}
        isOpen={modalData.openOption === 'DELETE'}
        onClose={() =>
          setModalData((prevData) => ({ ...prevData, openOption: '' }))
        }
        onDelete={onDelete}
      />
      <div className='relative mt-6'>
        <TableOperationHeader
          searchProps={{
            placeholder: intl.get('SEARCH_PLACEHOLDER'),
          }}
          inputValue={search}
          setInputValue={onTitleSearch}
          className='border-0 bg-neutral-white'
        />
        {hasLegacyOption ? (
          <div className='absolute top-2 right-4'>
            <MoreActionsPopup
              hasSplitButton
              splitButtonOnClick={goToAdvancedForm}
              buttonText={intl.get('SETTINGS_PAGE.FORMS.CREATE_FORM')}
              buttonTestId='add-form_button'
              options={[
                {
                  label: intl.get('SETTINGS_PAGE.FORMS.CREATE_LEGACY_FORM'),
                  selectOption: (close) => {
                    close();
                    goToLegacyForm();
                  },
                  dataCy: 'create-legacy-form_button',
                },
              ]}
            />
          </div>
        ) : isAdvancedCreate ? (
          <Button
            data-cy='add-form_button'
            className='absolute top-2 right-4'
            onClick={goToAdvancedForm}
          >
            {intl.get('SETTINGS_PAGE.FORMS.CREATE_FORM')}
          </Button>
        ) : (
          <Link
            to={`${PATHS.SETTINGS}${PATHS.FORM_PAGE}`}
            className='absolute top-2 right-4'
          >
            <Button data-cy='add-form_button'>
              {intl.get('SETTINGS_PAGE.FORMS.CREATE_FORM')}
            </Button>
          </Link>
        )}
      </div>
      <div
        className={tailwindOverride('w-full bg-neutral-white rounded-b', {
          'border-t border-b border-neutral-lighter-two': !isTableEmpty,
        })}
      >
        <Table
          className={tailwindOverride('w-full', {
            'border-t-0 border-b-0': !isTableEmpty,
          })}
          canSelectRows={false}
          bodyProps={{
            className: classnames('max-h-forms-table-body overflow-y-auto', {
              block: !isTableEmpty,
            }),
          }}
          data-testid='forms-table'
          data={{
            headData: {
              className: 'w-full block h-auto min-h-10',
              rowProps: {
                className: 'table w-full table-fixed h-auto min-h-10',
              },
              stickyHeader: true,
              handleSort,
              headCells: [
                {
                  columnName: FORMS_FILTER_OPTIONS.TITLE,
                  content: intl.get('SETTINGS_PAGE.FORMS.TABLE_HEAD.NAME'),
                  'data-cy': 'form-title',
                  className: tailwindOverride('w-3/12 2xl:w-4/12'),
                },
                {
                  columnName: FORMS_FILTER_OPTIONS.STATUS,
                  content: intl.get('REQUESTS_LIST_PAGE.TABLE.HEAD.STATUS'),
                  'data-cy': 'form-status',
                  className: tailwindOverride('w-30'),
                },
                {
                  columnName: FORMS_FILTER_OPTIONS.REQUEST_TYPE,
                  content: intl.get('SETTINGS_PAGE.FORMS.REQUEST_TYPE'),
                  'data-cy': 'form-request-type',
                  className: 'w-2/12 2xl:w-2/12',
                },
                {
                  columnName: FORMS_FILTER_OPTIONS.FORM_CREATOR,
                  content: intl.get('SETTINGS_PAGE.FORMS.TABLE_HEAD.CREATOR'),
                  'data-cy': 'form-creator',
                  className: tailwindOverride('w-48'),
                },
                {
                  columnName: FORMS_FILTER_OPTIONS.CREATION_DATE,
                  content: intl.get('SETTINGS_PAGE.FORMS.TABLE_HEAD.CREATED'),
                  'data-cy': 'form-creation-date',
                  className: 'w-30',
                },
                {
                  columnName: FORMS_FILTER_OPTIONS.LAST_UPDATE,
                  content: intl.get(
                    'SETTINGS_PAGE.FORMS.TABLE_HEAD.LAST_UPDATED'
                  ),
                  'data-cy': 'form-last-update',
                  className: 'w-30',
                },
                {
                  className: 'w-20',
                },
              ],
            },
            rows: forms.map((form) => {
              const isAdvancedForm = advancedIntake && !!form.isJsonSchema;
              const menuOptions = FORMS_TABLE_OPTIONS.filter((option) => {
                if (option === 'COPY_LINK') return isAdvancedForm;
                if (option === 'DELETE')
                  return !get(form, 'data.published', false);
                return true;
              });
              return {
                id: form.id,
                'data-cy': `form-table__row-${form.id}`,
                className: 'cursor-pointer table w-full table-fixed',
                onClick: (event: any) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (advancedIntake && form.isJsonSchema) {
                    window.location.href = `${V2_PATHS.FORM_BUILDER}/${form.id}?accountId=${organizationId}`;
                  } else {
                    history.push(
                      `${PATHS.SETTINGS}${PATHS.FORM_PAGE}/${form.id}?accountId=${organizationId}`
                    );
                  }
                },
                cells: [
                  {
                    content:
                      advancedIntake && form.isJsonSchema ? (
                        <a
                          href={`${V2_PATHS.FORM_BUILDER}/${form.id}?accountId=${organizationId}`}
                          title={get(form, 'title') || '-'}
                        >
                          <span>{get(form, 'title') || '-'}</span>
                        </a>
                      ) : (
                        <Link
                          to={`${PATHS.SETTINGS}${PATHS.FORM_PAGE}/${form.id}`}
                          title={get(form, 'title') || '-'}
                        >
                          <span>{get(form, 'title') || '-'}</span>
                        </Link>
                      ),
                    className: 'w-3/12 2xl:w-4/12 truncate',
                  },
                  {
                    content: (
                      <Tag
                        label={status[get(form, 'data.published', false)]}
                        {...statusColor[get(form, 'data.published', false)]}
                      />
                    ),
                    className: 'w-30',
                  },
                  {
                    content:
                      intl.get(
                        `REQUEST_PAGE.LEFT_PANEL.REQUEST_TYPE.${get(
                          form,
                          'request_type'
                        )}`
                      ) || '-',
                    title:
                      intl.get(
                        `REQUEST_PAGE.LEFT_PANEL.REQUEST_TYPE.${get(
                          form,
                          'request_type'
                        )}`
                      ) || '-',
                    className: 'w-2/12 2xl:w-2/12 truncate',
                  },
                  {
                    content: get(form, 'formCreator', false) ? (
                      <div className='flex items-center'>
                        <UserAvatar user={get(form, 'formCreator')} />
                        <TruncatedTableColumn
                          value={`${get(
                            form,
                            'formCreator.data.firstName',
                            ''
                          )} ${get(form, 'formCreator.data.lastName', '')}`}
                          className='ml-2.5'
                          content={
                            <span
                              className={tailwindOverride('ml-2.5', {
                                'text-neutral-light': isUserStatusDisabled(
                                  get(form, 'formCreator')
                                ),
                              })}
                            >
                              {`${get(
                                form,
                                'formCreator.data.firstName',
                                ''
                              )} ${get(form, 'formCreator.data.lastName', '')}`}
                            </span>
                          }
                        />
                      </div>
                    ) : form.default_system_request_form ? (
                      <div className='flex items-center'>
                        <Avatar
                          iconSrc={cognotaAvatar}
                          iconProps={{ className: 'text-lg' }}
                          bgColorClass='bg-secondary'
                        />
                        <Typography
                          variant='body'
                          className='ml-2.5 text-primary font-semibold'
                        >
                          {intl.get('SETTINGS_PAGE.PROJECTS_PAGE.SYSTEM')}
                        </Typography>
                      </div>
                    ) : (
                      <DeletedUser />
                    ),
                    className: 'w-48',
                  },
                  {
                    content: moment(form.createdAt).format(DATE.SHORT_FORMAT),
                    className: 'w-30',
                  },
                  {
                    content: moment(form.updatedAt).format(DATE.SHORT_FORMAT),
                    className: 'w-30',
                  },
                  {
                    onClick: (e: MouseEvent<HTMLDivElement>) =>
                      e.stopPropagation(),
                    content: (
                      <div className='w-full h-full text-center relative'>
                        <OverflowMenu
                          menuButtonProps={{
                            className: 'h-5',
                            'data-cy': 'form-actions_menu',
                          }}
                        >
                          {menuOptions.map((option) => (
                            <OverflowMenuItem
                              key={option}
                              onSelect={() => handleMenuSelect(option, form)}
                              data-cy={`${option}-${form.id}`}
                            >
                              {intl.get(
                                `SETTINGS_PAGE.FORMS.TABLE_MENU.${option}`
                              )}
                            </OverflowMenuItem>
                          ))}
                        </OverflowMenu>
                      </div>
                    ),
                    className: 'w-20',
                  },
                ],
              };
            }),
          }}
          emptyComponent={
            !isEmpty(search) ? (
              <RenderNoRecords
                imageSrc={noFormsFound}
                caption={intl.get('REQUESTS_LIST_PAGE.NO_RECORDS')}
                className='h-empty-forms-table-body'
                imageClassName='-ml-3'
              />
            ) : (
              <EmptyForms />
            )
          }
        />
      </div>
    </Fragment>
  );
};

export default FormsTable;
